/**
 * Ecomyras — Wishlist (additive feature, not part of native Horizon).
 *
 * Horizon ships no wishlist of any kind, so this file implements a small,
 * dependency-free, client-side-only wishlist:
 *
 *   - Wishlisted products are stored as an array of product HANDLES in
 *     localStorage under WISHLIST_STORAGE_KEY.
 *   - There is NO server-side / customer-account persistence. The list
 *     lives only in the browser that added it: clearing site data, or
 *     opening the store on another device, starts an empty wishlist.
 *     This is a deliberate v1 scope decision — see the project report
 *     for the reasoning — not an oversight.
 *   - Every mutation dispatches a `wishlist:updated` CustomEvent on
 *     `document` with `detail: { handles, count }`, so any interested
 *     UI (the header bubble below, the drawer, or a future product-page
 *     "add to wishlist" button — out of scope for this change) can react
 *     without polling localStorage itself.
 *
 * Public API is exposed two ways so future code can use whichever fits:
 *   - ES module named exports (getWishlist, isInWishlist, addToWishlist,
 *     removeFromWishlist, toggleWishlist).
 *   - `window.Ecomyras.wishlist.*` mirror, for any future non-module
 *     script (e.g. a theme app extension) that can't import this module.
 */

const WISHLIST_STORAGE_KEY = 'ecomyras:wishlist';
const WISHLIST_UPDATED_EVENT = 'wishlist:updated';

/** @returns {string[]} Product handles currently in the wishlist. */
export function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    // Corrupt or inaccessible storage (e.g. private browsing) — treat as empty
    // rather than throwing, so the header icon never breaks the page.
    return [];
  }
}

/** @param {string[]} handles */
function setWishlist(handles) {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(handles));
  } catch (_error) {
    // Storage full/unavailable — the event still fires below so the UI
    // reflects the in-memory intent for this page view.
  }

  document.dispatchEvent(
    new CustomEvent(WISHLIST_UPDATED_EVENT, {
      detail: { handles, count: handles.length },
    })
  );
}

/** @param {string} handle @returns {boolean} */
export function isInWishlist(handle) {
  return getWishlist().includes(handle);
}

/** @param {string} handle */
export function addToWishlist(handle) {
  if (!handle || isInWishlist(handle)) return;
  setWishlist([...getWishlist(), handle]);
}

/** @param {string} handle */
export function removeFromWishlist(handle) {
  setWishlist(getWishlist().filter((existingHandle) => existingHandle !== handle));
}

/**
 * @param {string} handle
 * @returns {boolean} The new "in wishlist" state after toggling.
 */
export function toggleWishlist(handle) {
  if (isInWishlist(handle)) {
    removeFromWishlist(handle);
    return false;
  }
  addToWishlist(handle);
  return true;
}

window.Ecomyras = window.Ecomyras || {};
window.Ecomyras.wishlist = { getWishlist, isInWishlist, addToWishlist, removeFromWishlist, toggleWishlist };

/**
 * Header wishlist icon. Deliberately mirrors `assets/cart-icon.js`'s
 * `CartIcon` component so the bubble reuses the exact same markup
 * contract (`.cart-bubble`, `.cart-bubble__background`, `.cart-bubble__text`)
 * and therefore the exact same CSS and "pop" animation
 * (`.cart-bubble--animating`, `@keyframes grow` / `move-and-fade` in
 * snippets/header-actions.liquid) with no new animation code.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bubble
 * @property {HTMLElement} bubbleCount
 */
class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('[data-wishlist-bubble]');
    this.bubbleCount = this.querySelector('[data-wishlist-bubble-count]');
    if (!this.bubble || !this.bubbleCount) return;

    this._onWishlistUpdate = this._onWishlistUpdate.bind(this);
    document.addEventListener(WISHLIST_UPDATED_EVENT, this._onWishlistUpdate);

    // Initial paint: reflect current localStorage state, no animation.
    this._render(getWishlist().length, false);
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_UPDATED_EVENT, this._onWishlistUpdate);
  }

  /** @param {CustomEvent<{count: number}>} event */
  _onWishlistUpdate(event) {
    this._render(event.detail.count, true);
  }

  /**
   * @param {number} count
   * @param {boolean} animate
   */
  _render(count, animate) {
    this.bubbleCount.textContent = count > 0 ? (count < 100 ? String(count) : '') : '';
    this.bubbleCount.classList.toggle('hidden', count === 0);
    this.bubble.classList.toggle('visually-hidden', count === 0);
    // Reuses the native "donut mask" cutout rule that already targets this
    // class (see .header-actions__cart-icon--has-cart in header-actions.liquid).
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    if (!animate || count === 0) return;

    requestAnimationFrame(() => {
      this.bubble.classList.add('cart-bubble--animating');
      const text = this.bubble.querySelector('.cart-bubble__text');
      const onEnd = () => {
        this.bubble.classList.remove('cart-bubble--animating');
        text?.removeEventListener('animationend', onEnd);
      };
      text?.addEventListener('animationend', onEnd);
    });
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

/**
 * Escapes a string for safe interpolation into innerHTML. Product data
 * below comes from this store's own /products/{handle}.js endpoint, but
 * we escape anyway as cheap, defensive good practice.
 * @param {string} value
 */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * Populates the wishlist drawer (see snippets/wishlist-drawer.liquid) with
 * the actual wishlisted products.
 *
 * Trade-off, documented for the merchant: there is no cart/customer object
 * available client-side to look up arbitrary products by handle, so
 * product data is fetched on demand from Shopify's public
 * `/products/{handle}.js` AJAX endpoint whenever the drawer opens or the
 * wishlist changes. This keeps the feature app-free and avoids any
 * server-side rendering work, at the cost of one small JSON fetch per
 * wishlisted product per drawer open.
 */
async function renderWishlistDrawer() {
  const container = document.getElementById('wishlist-drawer-content');
  if (!container) return;

  const emptyState = container.querySelector('[data-wishlist-empty-state]');
  const list = container.querySelector('[data-wishlist-list]');
  if (!emptyState || !list) return;

  const handles = getWishlist();

  if (handles.length === 0) {
    emptyState.hidden = false;
    list.hidden = true;
    list.innerHTML = '';
    return;
  }

  emptyState.hidden = true;
  list.hidden = false;

  // Fetch each product's public JSON. Failures (deleted/unpublished
  // product, offline, etc.) are caught per item so one bad handle
  // doesn't blank the whole drawer.
  const products = await Promise.all(
    handles.map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
    )
  );

  list.innerHTML = products
    .map((product, index) => {
      if (!product) return '';

      const handle = handles[index];
      const image = product.featured_image || product.images?.[0];
      const price = (product.price / 100).toFixed(2);

      return `
        <li class="wishlist-drawer__item">
          <a href="/products/${escapeHtml(product.handle)}" class="wishlist-drawer__item-link">
            ${
              image
                ? `<img
                    src="${escapeHtml(image)}"
                    width="72"
                    height="72"
                    alt=""
                    class="wishlist-drawer__item-image"
                    loading="lazy"
                  >`
                : ''
            }
            <span class="wishlist-drawer__item-info">
              <span class="wishlist-drawer__item-title">${escapeHtml(product.title)}</span>
              <span class="wishlist-drawer__item-price">${price} €</span>
            </span>
          </a>
          <button
            type="button"
            class="wishlist-drawer__item-remove button-unstyled"
            data-wishlist-remove="${escapeHtml(handle)}"
            aria-label="Retirer de la liste de souhaits"
          >&times;</button>
        </li>`;
    })
    .join('');
}

// Re-render whenever the wishlist changes anywhere on the page.
document.addEventListener(WISHLIST_UPDATED_EVENT, renderWishlistDrawer);

// Re-render when the drawer is opened, in case it changed on another tab.
document.addEventListener('theme-drawer:open', (event) => {
  const target = /** @type {HTMLElement | null} */ (event.target);
  if (target?.id === 'wishlist-drawer') renderWishlistDrawer();
});

// Event delegation for "remove" buttons, since drawer content is re-rendered
// dynamically and static listeners would be lost each time.
document.addEventListener('click', (event) => {
  const removeButton = /** @type {HTMLElement | null} */ (event.target).closest('[data-wishlist-remove]');
  if (!removeButton) return;
  removeFromWishlist(removeButton.getAttribute('data-wishlist-remove'));
});

// Populate the drawer once on load so it's correct even before first open
// (e.g. if a future product-page button needs to read live state).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderWishlistDrawer);
} else {
  renderWishlistDrawer();
}
